import { Button, Placeholder, Spinner } from '@telegram-apps/telegram-ui'
import { useEffect } from 'react'
import { isTelegram } from 'shared/lib/telegram/platform'
import { refreshTelegramSession } from 'shared/lib/auth/recovery'
import { useTranslate } from 'shared/lib/i18n'
import { useAuthStore } from 'shared/store/auth'
import { AdminLogin } from './AdminLogin'
import { useDevTap } from 'features/dev-tools'

export function AuthGate({ children }: { children: React.ReactNode }) {
	const { t } = useTranslate()
	const status = useAuthStore((s) => s.status)
	const error = useAuthStore((s) => s.error)

	const devTap = useDevTap()
	const hasInvalidTelegramInitData = error === 'Invalid Telegram initData'
	const retryTelegramLogin = () => {
		useAuthStore.getState().setError(null)
		refreshTelegramSession().catch((e: unknown) => {
			if (useAuthStore.getState().status !== 'authenticated') {
				useAuthStore
					.getState()
					.setError(e instanceof Error ? e.message : 'Authentication failed')
			}
		})
	}

	useEffect(() => {
		if (!isTelegram) return
		// In dev mode, the cached session bootstrapped in main.tsx is enough —
		// skip re-validating Telegram initData against the backend.
		if (
			import.meta.env.DEV &&
			useAuthStore.getState().status === 'authenticated'
		)
			return
		// Dev-override sessions (from account switcher) must not be overwritten
		// by the real Telegram user's initData in any build mode.
		const cached = localStorage.getItem('@auth:telegram:v3')
		if (cached) {
			try {
				const parsed = JSON.parse(cached) as { isDevOverride?: boolean }
				if (parsed.isDevOverride) return
			} catch {
				// corrupted — proceed with normal flow
			}
		}
		let cancelled = false
		useAuthStore.getState().setError(null)
		refreshTelegramSession().catch((e: unknown) => {
			if (!cancelled && useAuthStore.getState().status !== 'authenticated') {
				useAuthStore
					.getState()
					.setError(e instanceof Error ? e.message : 'Authentication failed')
			}
		})
		return () => {
			cancelled = true
		}
	}, [])

	if (status !== 'authenticated') {
		// Browser (non-Telegram): show the admin username/password login.
		if (!isTelegram) return <AdminLogin />
		// Telegram: auto sign-in is in flight. These states render outside
		// AppLayout, so clear Telegram's fullscreen controls here too.
		if (error)
			return (
				<div className='pt-(--header-safe-top)'>
					<Placeholder
						header={t('pages.auth.loginErrorTitle')}
						description={
							hasInvalidTelegramInitData
								? t('pages.auth.invalidTelegram')
								: error === 'Failed to fetch'
									? t('pages.auth.noConnection')
									: error
						}
						// onClick={error === 'Failed to fetch' ? devTap : undefined}
						onClick={devTap}
					>
						<Button size='m' onClick={retryTelegramLogin}>
							{t('pages.auth.retryLogin')}
						</Button>
					</Placeholder>
				</div>
			)
		return (
			<div className='pt-(--header-safe-top)'>
				<Placeholder description={t('pages.auth.signingIn')}>
					<Spinner size='l' />
				</Placeholder>
			</div>
		)
	}

	return <>{children}</>
}
